<template>
  <router-view v-slot="{ Component }">
    <transition :name="transitionName" mode="out-in">
      <component v-if="!isLoading" :is="Component" />
      <div v-else class="loading-placeholder">Загрузка...</div>
    </transition>
  </router-view>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRouter } from "vue-router";

export default defineComponent({
  name: "App",
  setup() {
    const isTransitioning = ref(true);
    const router = useRouter();
    const isLoading = ref(true); // Состояние загрузки
    const transitionName = ref("default"); // Стандартный переход

    // Слушатель изменения маршрута
    router.beforeEach((to, from, next) => {
      if (to.name === "DoctorsPage" && from.name === "DoctorProfilePage") {
        transitionName.value = "slide-right";
      } else if (to.name === "HomeView" && from.name === "DoctorsPage") {
        transitionName.value = "slide-right";
      } else if (
        to.name === "DoctorProfilePage" &&
        from.name === "DoctorsPage"
      ) {
        transitionName.value = "slide-left";
      } else if (to.name === "DoctorsPage" && from.name === "HomeView") {
        transitionName.value = "none";
      } else if (to.name === "DoctorsPage") {
        transitionName.value = "slide-left";
      } else {
        transitionName.value = "slide-up";
      }
      next();
    });
    onMounted(() => {
      setTimeout(() => {
        isLoading.value = false; // Завершение загрузки
      }, 1000); // Симуляция загрузки
    });
    return {
      isTransitioning,
      transitionName,
      isLoading, // Возвращаем isLoading
    };
  },
});
</script>

<style lang="scss">
body {
  background-color: #f8f8f8;
  scroll-behavior: smooth;
}
.loading-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  font-size: 18px;
  font-weight: bold;
}
#app {
  font-family: "Roboto", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: var(--bs-primary);
    }
  }
}
main.main {
  flex: 1 1 auto;
}

/* Анимация для переходов */
/* Сдвиг вверх */
.slide-up-enter-active,
.slide-up-leave-active {
  position: absolute; /* Оба компонента перекрываются */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter {
  transform: translateY(100%); /* Страница начинает снизу */
  opacity: 0;
}

.slide-up-enter-to {
  transform: translateY(0); /* На месте */
  opacity: 1;
}

.slide-up-leave {
  z-index: 2;
  transform: translateY(0); /* На месте */
  opacity: 1;
}

.slide-up-leave-to {
  transform: translateY(-100%); /* Уходит вверх */
  opacity: 0;
}

/* Сдвиг вниз */
.slide-down-enter-active,
.slide-down-leave-active {
  position: absolute; /* Оба компонента перекрываются */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter {
  z-index: 1;
  transform: translateY(-100%); /* Начинает сверху */
  opacity: 0;
}

.slide-down-enter-to {
  z-index: 1;
  transform: translateY(0); /* На месте */
  opacity: 1;
}

.slide-down-leave {
  z-index: 2;
  transform: translateY(0); /* На месте */
  opacity: 1;
}

.slide-down-leave-to {
  z-index: 2;
  transform: translateY(100%); /* Уходит вниз */
  opacity: 0;
}

/* Сдвиг влево */
.slide-left-enter-active,
.slide-left-leave-active {
  position: absolute; /* Оба компонента перекрываются */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter {
  transform: translateX(100%); /* Начинает справа */
  opacity: 0;
}

.slide-left-enter-to {
  transform: translateX(0); /* На месте */
  opacity: 1;
}

.slide-left-leave {
  transform: translateX(0); /* На месте */
  opacity: 1;
}

.slide-left-leave-to {
  transform: translateX(-100%); /* Уходит влево */
  opacity: 0;
}

/* Сдвиг вправо */
.slide-right-enter-active,
.slide-right-leave-active {
  position: absolute; /* Оба компонента перекрываются */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-right-enter {
  transform: translateX(-100%); /* Начинает слева */
  opacity: 0;
}

.slide-right-enter-to {
  transform: translateX(0); /* На месте */
  opacity: 1;
}

.slide-right-leave {
  transform: translateX(0); /* На месте */
  opacity: 1;
}

.slide-right-leave-to {
  transform: translateX(100%); /* Уходит вправо */
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  position: absolute;
  z-index: -1;
}

.slide-up-leave {
  z-index: 200; /* Старый компонент виден поверх нового */
}

.scrollable-content {
  margin-top: 60px;
  overflow-x: hidden;
  padding-bottom: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex-grow: 1;
  transform: translate3d(0px, 0px, 0px);
  scroll-behavior: smooth;
  color: var(--p-drawer-color);
  display: block;
}

.weight-picker {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 8px;
}
</style>
